import { LifecycleTracker } from '@/infra/lifecycle-tracker'
import type { ActivationLifecycle } from '@/infra/activation'
import { action, computed, observable, runInAction } from 'mobx'
import { task } from 'mobx-task'
import type { Id } from '@/infra/id'
import {
  InfiniteScrollProvider,
  syncInfiniteScrollerItems,
} from '@/infra/InfiniteScrollProvider'
import type { Conversation } from './Conversation'
import type { ConversationStore } from './ConversationStore'
import type { SnackbarRetryWrapperFactory } from '@/features/shell'
import type { Message } from './Message'
import { memoize } from '@/utils/memoize-util'
import {
  combineLatestWith,
  delay,
  distinctUntilChanged,
  filter,
  firstValueFrom,
  map,
  type Observable,
  of,
  pairwise,
  startWith,
  switchMap,
} from 'rxjs'
import { asAtom, tapAction, toStream } from '@/utils/mobx-util'
import { filterNonNil, fromAbortSignal } from '@/utils/rx-util'
import { ViewModelList } from '@/infra/view-model-list'
import { ConversationMessageViewModel } from '@/features/messages/ConversationMessageViewModel'
import type { PageActivityDescriptor } from '@/infra/page-activity'
import { takeUntil } from 'rxjs/operators'

/**
 * View model for a conversation.
 */
export class ConversationViewModel implements ActivationLifecycle {
  private readonly _lifecycle = new LifecycleTracker()

  /**
   * Tracks whether the user is paying attention to the screen.
   *
   * @private
   */
  @observable private accessor _isUserPayingAttention = false

  /**
   * List of message view models.s
   *
   * @private
   */
  private readonly _messageVms = new ViewModelList({
    models: () => this.messagesScroller.items.slice(),
    create: (m) =>
      new ConversationMessageViewModel(
        this._conversationStore,
        () => this._isUserPayingAttention,
        m,
      ),
    activate: (vm) => vm.activate(),
    deactivate: (vm) => vm.deactivate(),
  })

  /**
   * Debounce changes to the unread count to avoid flickering the badge
   * when going from zero to non-zero and then back to zero.
   *
   * @private
   */
  private readonly _debouncedUnreadCount = asAtom(
    toStream(() => this._conversation?.unreadCount ?? 0, true).pipe(
      // Make sure we always start out with zero to prime the pairwise operator.
      startWith(0),
      pairwise(),
      switchMap(([prev, current]) =>
        prev === 0 && current > 0
          ? // It went from zero to non-zero, delay it so it changes rapidly back
            // to zero we won't be flashing the unread badge.
            of(current).pipe(delay(300))
          : of(current),
      ),
      distinctUntilChanged(),
    ),
    0,
  )

  /**
   * Whether the messages have been fetched at least once.
   * Used to determine when we should start syncing messages.
   */
  @observable private accessor _messagesFetchedOnce = false

  /**
   * The loaded conversation.
   */
  @observable private accessor _conversation: Conversation | null = null

  /**
   * The text of the message to be sent.
   */
  @observable accessor messageText = ''

  /**
   * An observable stream that emits the current conversation ID when
   * the view model is active, or `null` otherwise.
   * This is used to determine whether the user is actively viewing
   * the conversation.
   */
  readonly activeConversationId$ = toStream(
    () => this.conversationId,
    true,
  ).pipe(
    combineLatestWith(this._lifecycle.active$),
    map(([conversationId, active]) => (active ? conversationId : null)),
    distinctUntilChanged(),
  )

  /**
   * Infinite scroll provider for loading messages.
   */
  readonly messagesScroller = new InfiniteScrollProvider<Message>({
    limit: 200,
    fetchItems: async ({ paging, signal }) => {
      const withRetry = this._createSnackbarRetryWrapper(
        'Unable to load messages',
        signal,
      )

      return await withRetry(() =>
        this._conversationStore.listMessages(
          this.conversationId,
          paging,
          signal,
        ),
      )
    },
  })

  constructor(
    private readonly _getConversationId: () => Id,
    private readonly _conversationStore: ConversationStore,
    private readonly _pageActivity$: Observable<PageActivityDescriptor>,
    private readonly _userActive$: Observable<boolean>,
    private readonly _createSnackbarRetryWrapper: SnackbarRetryWrapperFactory,
  ) {
    this._lifecycle.manage(this._messageVms)

    this.fetchConversation = this.fetchConversation.wrap((f) =>
      memoize(f, {
        promise: true,
      }),
    )

    this.fetchMessages = this.fetchMessages.wrap((f) =>
      memoize(f, {
        promise: true,
      }),
    )

    this._lifecycle.manage(
      this._pageActivity$.pipe(
        combineLatestWith(this._userActive$),
        map(
          ([pageActivity, userActive]) =>
            pageActivity.visible && pageActivity.focused && userActive,
        ),
        distinctUntilChanged(),
        tapAction((isPayingAttention) => {
          this._isUserPayingAttention = isPayingAttention
        }),
      ),
    )

    toStream(() => this._messagesFetchedOnce, true)
      .pipe(
        filter(Boolean),
        switchMap(() => toStream(() => this._conversation, true)),
        filterNonNil(),
        switchMap((conversation) =>
          syncInfiniteScrollerItems({
            scroller: this.messagesScroller,
            additions$: conversation.messages.additions$,
            removals$: conversation.messages.removals$,
          }),
        ),
      )
      .subscribe()
  }

  /**
   * The conversation ID.
   */
  @computed get conversationId() {
    return this._getConversationId()
  }

  /**
   * The current unread messages count.
   */
  @computed get unreadCount() {
    return this._debouncedUnreadCount()
  }

  /**
   * The conversation, if loaded.
   */
  @computed get conversation() {
    return this._conversation
  }

  /**
   * Gets all the loaded messages as view models.
   */
  @computed
  get messages() {
    // Sort in ascending order by date.
    return this._messageVms.items
      .slice()
      .sort(
        (a, b) =>
          a.message.dateCreated.getTime() - b.message.dateCreated.getTime(),
      )
  }

  /**
   * Activates the view model.
   *
   * Does not fetch the conversation or messages, this must be done
   * by the consumer at the appropriate time, which may even be while this
   * view model is inactive.
   */
  activate() {
    this._lifecycle.activate()
  }

  /** @inheritDoc */
  deactivate() {
    this._lifecycle.deactivate()
  }

  /**
   * Fetches the conversation and the first page of messages.
   * NOTE: This is memoized.
   */
  fetchConversation = task(async (signal?: AbortSignal) => {
    const withRetry = this._createSnackbarRetryWrapper(
      'Unable to load conversation',
      signal,
    )

    // Waits until the conversation ID getter returns something not null.
    const conversationIdProvided$ = toStream(
      () => this.conversationId,
      true,
    ).pipe(filterNonNil(), takeUntil(fromAbortSignal(signal)))
    await firstValueFrom(conversationIdProvided$, { defaultValue: null })
    await withRetry(() => this._fetchConversation(signal))
  })

  /**
   * Fetches the messages for the conversation.
   * NOTE: This is memoized.
   */
  fetchMessages = task(async (signal?: AbortSignal) => {
    // Make sure the conversation is loaded before fetching messages.
    if (!this._conversation) {
      await this.fetchConversation(signal)
    }

    // Fetch the messages.
    const page = await this.messagesScroller.fetch({ signal })
    if (page.next && page.data.length < this.messagesScroller.limit) {
      // The backend will sometimes return a next-page token despite not
      // having more items. We can detect this by checking the length of
      // the returned page being less than our limit.
      // If so, just perform another fetch right away so we don't have to wait
      // for the infinite scroll trigger to trigger it.
      await this.messagesScroller.more(signal)
    }

    runInAction(() => {
      this._messagesFetchedOnce = true
    })
  })

  /**
   * Sets the message text to be sent.
   */
  @action.bound
  setMessageText(text: string) {
    this.messageText = text
  }

  /**
   * Sends the current message text to the conversation.
   */
  @action.bound
  sendCurrentMessage() {
    if (!this.messageText.trim()) {
      return null
    }

    const text = this.messageText
    this.setMessageText('')
    void this.send(text)
  }

  /**
   * Sends a text message.
   */
  send = task.resolved(async (text: string) => {
    if (!this._conversation) {
      return
    }

    await this._conversationStore.sendTextMessage({
      conversation: this._conversation,
      text: text,
    })
  })

  /**
   * Fetches the conversation by ID.
   */
  private async _fetchConversation(signal?: AbortSignal) {
    if (signal?.aborted) {
      return
    }

    const conversation = await this._conversationStore.fetchConversation(
      this.conversationId,
    )

    if (conversation && !signal?.aborted) {
      runInAction(() => {
        this._conversation = conversation
      })
    }
  }
}
